import { combineLatest, map, of } from 'rxjs';

import { Injectable } from '@angular/core';

import { Store } from '@ngxs/store';

import { ApiInstanceService } from '@app/core/api/api-instance.service';
import { ApiService } from '@app/core/api/api.service';
import { AlertsApiRoute, ApiEndpoint } from '@app/core/models/api.models';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import {
  AlertCategory,
  AlertDefinition,
  AlertDigestInterval,
  AlertMuteInfo,
  AlertTableFilter,
} from './alert-settings.model';

@Injectable({
  providedIn: 'root',
})
export class AlertSettingsApiService extends ApiInstanceService {
  readonly endpoint = ApiEndpoint.Notifications;

  getDefinitions(filters?: Record<AlertTableFilter, []>) {
    const filterValues: any = {};
    if (filters?.[AlertTableFilter.Category]) filterValues.categoryId = filters[AlertTableFilter.Category];
    if (filters?.[AlertTableFilter.Severity]) filterValues.severity = filters[AlertTableFilter.Severity];

    return this.getAll<AlertDefinition>({
      route: [AlertsApiRoute.Definitions],
      filterValues,
    });
  }

  getCategories() {
    return this.getAll<AlertCategory>({
      route: [AlertsApiRoute.Categories],
      sortValues: { name: 1 },
    });
  }

  getMutes() {
    return this.getAll<AlertMuteInfo>({
      route: [AlertsApiRoute.Muting],
    });
  }

  getDigestInterval() {
    return this.getObject({
      route: [AlertsApiRoute.DigestInterval, this.store.selectSnapshot(CustomerState.customerId)],
    }).pipe(map(({ data }) => data));
  }

  setMutes(data) {
    return this.put({
      route: [AlertsApiRoute.Muting],
      data,
    }).pipe(
      map((res: any) => ({
        created: <AlertMuteInfo[]>res.data.created,
        updated: <AlertMuteInfo[]>res.data.updated,
      })),
    );
  }

  deleteMutes(data) {
    return this.delete({
      route: [AlertsApiRoute.Muting],
      data,
    }).pipe(map((res: any) => <AlertMuteInfo[]>res.data.deleted));
  }
  updateMutes(deletes, edits) {
    const $ = [];
    if (deletes.length) $.push(this.deleteMutes(deletes));
    else $.push(of(null));
    if (edits.length) $.push(this.setMutes(edits));
    else $.push(of(null));

    return combineLatest($).pipe(
      map((res: any) => {
        const returnValue: any = {};
        if (!!res[0]?.length) returnValue.delete = res[0];

        if (!!res[1]) {
          returnValue.update = res[1].updated;
          returnValue.create = res[1].created;
        }

        return returnValue;
      }),
    );
  }

  setDigestInterval(data) {
    return this.put({
      route: [AlertsApiRoute.DigestInterval, this.store.selectSnapshot(CustomerState.customerId)],
      data,
    });
  }

  deleteDigestInterval() {
    return this.setDigestInterval({ interval: AlertDigestInterval.Never });
  }

  constructor(
    apiService: ApiService,
    private store: Store,
  ) {
    super(apiService);
  }
}
